import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

import { OrganizationMembersProvider } from '@shared/sections/OrganizationMembers/context';

import Autocomplete from '@shared/atoms/Autocomplete';
import T from '@shared/atoms/Typography';
import OrganizationMembers from '@shared/sections/OrganizationMembers';
import theme from '@shared/themes/default';

import { useAppGlobals } from '../../contexts/AppContext';
import { listOrganizationOptions } from '../../../utils';

import { getOrganization, listOrganizations } from './graphql/queries';

const AutocompleteWrapper = styled.div`
  max-width: 400px;
`;

const MembersView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    databaseUser,
    getRestClient,
    locale,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    useNewGraphQLQuery,
    user,
  } = useAppGlobals();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const preSelectedOrganizationId = searchParams.get('organization');
  const preSelectedUserIdToManageRole = searchParams.get('user');
  const paramsView = searchParams.get('view');
  const paramsManage = searchParams.get('manage');

  const [organization, setOrganization] = useState(null);
  const [organizationList, setOrganizationList] = useState([]);

  const [organizationIdUrl, setOrganizationIdUrl] = useState();

  const listOrganizationsResponse = useNewGraphQLQuery(listOrganizations, {
    variables: {
      pagination: { limit: 1000 },
    },
  });
  const [getOrganizationQuery, getOrganizationResponse] =
    useLazyGraphQLQuery(getOrganization);

  useEffect(() => {
    const { loading, data } = listOrganizationsResponse;
    if (!loading && data?.listOrganizations) {
      setOrganizationList(data.listOrganizations);
    }
  }, [listOrganizationsResponse]);

  const selectOrganizationHandler = (organization) => {
    if (organization) {
      const { value: organizationId } = organization;
      navigate(`/members?organization=${organizationId}&view=members`);
    } else {
      navigate('/members');
    }
  };

  const onViewGroupActivity = (organizationId, groupId) => {
    navigate(
      `/activity?organization=${organizationId}&view=groups&id=${groupId}`
    );
  };

  const handleHistory = (queryParams) => {
    if (organizationIdUrl) {
      navigate(`/members?organization=${organizationIdUrl}&${queryParams}`);
    } else {
      navigate('/members');
    }
  };

  useEffect(() => {
    if (organizationIdUrl) {
      setOrganization(
        organizationList.find(
          (organization) => organization.id === organizationIdUrl
        )
      );
      getOrganizationQuery({
        variables: {
          id: organizationIdUrl,
        },
      });
    } else {
      setOrganization();
    }
  }, [organizationIdUrl]);

  useEffect(() => {
    const { loading, data } = getOrganizationResponse;
    if (!loading && data?.getOrganization) {
      setOrganization(data.getOrganization);
    }
  }, [getOrganizationResponse]);

  useEffect(() => {
    if (organizationList.length > 0) {
      if (preSelectedOrganizationId !== organizationIdUrl) {
        setOrganizationIdUrl(preSelectedOrganizationId);
      }
    }
  }, [preSelectedOrganizationId, organizationList]);

  let hasSubscription = false;
  let totalLicences = null;
  if (getOrganizationResponse?.data?.getOrganization?.subscription) {
    hasSubscription = true;
    totalLicences =
      getOrganizationResponse.data.getOrganization.subscription.seats ||
      'unlimited';
  }

  return (
    <div>
      <T
        color={theme.palette.lightPure}
        sx={{
          marginBottom: theme.spacing.m1,
        }}
        variant="subtitle"
      >
        {t('backoffice.general.findOrganization')}
      </T>
      <AutocompleteWrapper>
        <Autocomplete
          filterOptions
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.name}
          loading={listOrganizationsResponse.loading}
          onChange={selectOrganizationHandler}
          options={listOrganizationOptions(organizationList)}
          preSelectedValue={
            organization && {
              name: organization.name
                ? `${organization.archived ? '(Archived) ' : ''}${organization.name}`
                : `No name (ID: ${organization.id})`,
              value: organization.id,
            }
          }
        />
      </AutocompleteWrapper>
      {organizationIdUrl && (
        <OrganizationMembersProvider
          useGraphQLMutation={useGraphQLMutation}
          useLazyGraphQLQuery={useLazyGraphQLQuery}
          databaseUser={databaseUser}
          deleteOwnUser
          handleHistory={handleHistory}
          hasActiveScimClient={organization?.hasActiveScimClient || false}
          hasSubscription={hasSubscription}
          isBackOffice
          listView={paramsView}
          locale={locale}
          manageGroups={paramsManage === 'groups'}
          manageRoles={paramsManage === 'role'}
          onViewGroupActivity={(organizationId, groupId) =>
            onViewGroupActivity(organizationId, groupId)
          }
          organizationId={organizationIdUrl}
          preSelectedUserIdToManageRole={preSelectedUserIdToManageRole}
          restClient={getRestClient()}
          totalLicences={totalLicences}
          user={user}
        >
          <OrganizationMembers />
        </OrganizationMembersProvider>
      )}
    </div>
  );
};

export default MembersView;
